import { z } from 'zod'

export const CreditDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((day) => {
  const date = new Date(`${day}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === day
}, 'Expected a valid calendar date (YYYY-MM-DD)')

const CreditAccountSchema = z.object({
  team_id: z.string().trim().min(1).optional(),
  user_id: z.string().trim().min(1).optional(),
}).strict()

function validateAccount(input: z.infer<typeof CreditAccountSchema>, ctx: z.RefinementCtx) {
  if (input.team_id && input.user_id) {
    ctx.addIssue({ code: 'custom', message: 'Choose team_id or user_id, not both', path: ['user_id'] })
  }
}

export const CreditUsageInputSchema = CreditAccountSchema.extend({
  start_date: CreditDaySchema,
  end_date: CreditDaySchema,
}).superRefine((input, ctx) => {
  validateAccount(input, ctx)
  const span = Date.parse(input.end_date) - Date.parse(input.start_date)
  if (span < 0 || span > 89 * 86_400_000) {
    ctx.addIssue({ code: 'custom', message: 'Choose an inclusive range of 1–90 days', path: ['end_date'] })
  }
})
export const CreditUsageDayInputSchema = CreditAccountSchema.extend({ day: CreditDaySchema }).superRefine(validateAccount)
export type CreditUsageDayInput = z.infer<typeof CreditUsageDayInputSchema>
export type CreditUsageInput = z.infer<typeof CreditUsageInputSchema>

export const CreditTotalsSchema = z.object({
  gross_credits: z.number(),
  refunded_credits: z.number(),
  net_credits: z.number(),
  granted_credits: z.number(),
})
export type CreditTotals = z.infer<typeof CreditTotalsSchema>

export const CreditAdjustmentSchema = z.object({
  id: z.string(),
  kind: z.enum(['refund', 'grant']),
  credits: z.number(),
  posted_at: z.string(),
  reason: z.string().nullable(),
})

export const DailyCreditTaskSchema = CreditTotalsSchema.extend({
  task_id: z.string().nullable(),
  label: z.string(),
  kind: z.enum(['exploration', 'journey', 'task']),
  scan_date: z.string().nullable(),
  adjustments: z.array(CreditAdjustmentSchema),
})

export const DailyCreditProjectSchema = CreditTotalsSchema.extend({
  project_id: z.string().nullable(),
  project_name: z.string(),
  tasks: z.array(DailyCreditTaskSchema),
})
export type DailyCreditProject = z.infer<typeof DailyCreditProjectSchema>

export const DailyCreditSummarySchema = CreditTotalsSchema.extend({ day: CreditDaySchema })
export const DailyCreditDetailSchema = DailyCreditSummarySchema.extend({
  projects: z.array(DailyCreditProjectSchema),
  adjustments: z.array(CreditAdjustmentSchema),
})
export type DailyCreditDetail = z.infer<typeof DailyCreditDetailSchema>

export const DailyCreditUsageSchema = z.object({
  start_date: CreditDaySchema,
  end_date: CreditDaySchema,
  timezone: z.literal('UTC'),
  as_of: z.string(),
  available_credits: z.number().nullable(),
  balance_status: z.enum(['resolved', 'unavailable']),
  usage_waived: z.boolean(),
  days: z.array(DailyCreditSummarySchema),
})
export const CreditUsageDaySchema = DailyCreditDetailSchema.extend({
  timezone: z.literal('UTC'),
  as_of: z.string(),
})
export type CreditUsageDay = z.infer<typeof CreditUsageDaySchema>
export type DailyCreditUsage = z.infer<typeof DailyCreditUsageSchema>
